import type {
  DiscountRateCombination,
  DiscountRateLine,
  EnumOption,
  EnumsResponse,
  InterestType,
  RateCategory,
  ReserveCategory,
} from './types';
import {
  SLOT_CATEGORY_KEYS,
  WELL_STATUS_SLOT_CATEGORY_KEYS,
} from 'utils/slot-categories';

// ─────────────────────────────────────
// Category sets and labels
// ─────────────────────────────────────

/**
 * The Create New Well statuses, each priced at its own rate row (INS-13301), in the
 * slot_categories.json display order. The same four as
 * DiscountRateScenario::DiscountRateEntry::PRE_PERMIT_RESERVE_CATEGORIES.
 */
export const PRE_PERMIT_RESERVE_CATEGORIES: ReserveCategory[] = [
  ...WELL_STATUS_SLOT_CATEGORY_KEYS,
];

/**
 * Reserve categories whose hurdle rate must stay blank. Mirrors
 * DiscountRateScenario::DiscountRateEntry::NO_HURDLE_RESERVE_CATEGORIES.
 */
export const NO_HURDLE_RESERVE_CATEGORIES: ReserveCategory[] = [
  'pdp',
  ...PRE_PERMIT_RESERVE_CATEGORIES,
];

export const RATE_CATEGORIES: RateCategory[] = ['target', 'max', 'mtm'];
export const INTEREST_TYPES: InterestType[] = [
  'working_interest',
  'royalty_interest',
];
/** Reserve categories in the enums endpoint's order: the slot categories, then IRR. */
export const RESERVE_CATEGORIES: ReserveCategory[] = [
  ...SLOT_CATEGORY_KEYS,
  'irr',
];

/**
 * Reserve categories the area warns about when a rate category is missing. Every one:
 * a slot in any of them goes unvalued without a target, max and mtm rate.
 */
export const RATE_COMPLETENESS_RESERVE_CATEGORIES: ReserveCategory[] =
  RESERVE_CATEGORIES;

/** Display labels for rate categories, reserve categories and interest types, by value. */
export interface CategoryLabels {
  rateCategories: Record<string, string>;
  reserveCategories: Record<string, string>;
  interestTypes: Record<string, string>;
}

/**
 * The labels the enums endpoint serves, so messages name categories the way the selects do.
 * @param {Pick<EnumsResponse, 'rateCategories' | 'reserveCategories' | 'interestTypes'> | null} enums
 *   The loaded enums, or null while they load
 * @return {CategoryLabels} Each value's label; until the enums load, values show as themselves
 */
export function categoryLabels(
  enums: Pick<
    EnumsResponse,
    'rateCategories' | 'reserveCategories' | 'interestTypes'
  > | null
): CategoryLabels {
  /**
   * @param {EnumOption[]} options - One enum's options
   * @return {Record<string, string>} Their labels by value
   */
  const byValue = (options: EnumOption[] = []) =>
    Object.fromEntries(options.map((o) => [o.value, o.label]));
  return {
    rateCategories: byValue(enums?.rateCategories),
    reserveCategories: byValue(enums?.reserveCategories),
    interestTypes: byValue(enums?.interestTypes),
  };
}

/**
 * @param {Record<string, string>} labels - Labels by value
 * @param {string} value - A value
 * @return {string} Its label, or the value itself when the labels lack it
 */
function labelFor(labels: Record<string, string>, value: string): string {
  return labels[value] ?? value;
}

/**
 * @param {ReserveCategory | ''} reserveCategory - A row's reserve category
 * @return {boolean} True when the category carries no hurdle rate
 */
export function hasNoHurdle(reserveCategory: ReserveCategory | ''): boolean {
  return (
    !!reserveCategory && NO_HURDLE_RESERVE_CATEGORIES.includes(reserveCategory)
  );
}

/**
 * @param {DiscountRateLine} line - An editor line
 * @return {boolean} Whether its hurdle input is disabled: every selected reserve category is a
 *   no-hurdle one
 */
export function isLineHurdleDisabled(line: DiscountRateLine): boolean {
  return (
    line.reserveCategories.length > 0 &&
    line.reserveCategories.every(hasNoHurdle)
  );
}

/**
 * @template T
 * @param {T[]} values - Selected values
 * @param {T[]} order - Every value in display order
 * @return {T[]} The values, deduplicated, in display order
 */
export function inOrder<T>(values: T[], order: T[]): T[] {
  return order.filter((value) => values.includes(value));
}

// ─────────────────────────────────────
// Expand: lines → combinations
// ─────────────────────────────────────

/**
 * The natural key of a discount_rate_entries row — the columns its uniqueness validation is
 * scoped to. A blank operator and a null one are the same key, as they are on save.
 * @param {DiscountRateCombination} e - A combination
 * @return {string} rate|reserve|interest|operator
 */
export function combinationKey(
  e: Pick<
    DiscountRateCombination,
    'rateCategory' | 'reserveCategory' | 'interestType' | 'operator'
  >
): string {
  return `${e.rateCategory}|${e.reserveCategory}|${e.interestType}|${
    e.operator || ''
  }`;
}

/**
 * Every combination a line covers. A no-hurdle reserve category saves a blank hurdle even when
 * the line carries one for its other categories.
 * @param {DiscountRateLine} line - An editor line
 * @return {DiscountRateCombination[]} One combination per rate × reserve × interest selection
 */
export function expandLine(line: DiscountRateLine): DiscountRateCombination[] {
  const combinations: DiscountRateCombination[] = [];
  for (const interestType of line.interestTypes) {
    for (const reserveCategory of line.reserveCategories) {
      for (const rateCategory of line.rateCategories) {
        combinations.push({
          rateCategory,
          reserveCategory,
          interestType,
          operator: line.operator,
          rate: line.rate,
          hurdleRate: hasNoHurdle(reserveCategory) ? null : line.hurdleRate,
        });
      }
    }
  }
  return combinations;
}

/**
 * @param {DiscountRateLine[]} lines - An area's lines
 * @return {DiscountRateCombination[]} Every combination they cover, in line order
 */
export function expandLines(
  lines: DiscountRateLine[]
): DiscountRateCombination[] {
  return lines.flatMap(expandLine);
}

// ─────────────────────────────────────
// Collapse: combinations → lines
// ─────────────────────────────────────

interface Block {
  rateCategories: RateCategory[];
  reserveCategories: ReserveCategory[];
  interestTypes: InterestType[];
  operator: string;
  rate: number | '';
  hurdleRate: number | null;
  /** Position of the block's first combination, so lines keep the rows' order. */
  order: number;
}

/**
 * @param {number | string | null} value - A rate as loaded (the API sends decimals as strings)
 * @return {number | ''} The rate as a number, or blank
 */
function toRate(value: number | string | null): number | '' {
  return value === '' || value === null ? '' : Number(value);
}

/**
 * @param {number | string | null} value - A hurdle rate as loaded
 * @return {number | null} The hurdle as a number, or null when blank
 */
function toHurdle(value: number | string | null): number | null {
  return value === '' || value === null ? null : Number(value);
}

/**
 * Splits combinations that share an operator, rate and hurdle into rectangular blocks — each
 * block is exactly rateCategories × reserveCategories × interestTypes, and together they cover
 * every combination once. Reserve × interest pairs with the same rate categories go together,
 * then reserves with the same interest types.
 * @param {DiscountRateCombination[]} group - Combinations sharing operator, rate and hurdle
 * @param {number[]} orders - Each combination's position in the original list
 * @return {Block[]} The blocks
 */
function blocksOf(group: DiscountRateCombination[], orders: number[]): Block[] {
  const rateSets = new Map<string, { rates: RateCategory[]; order: number }>();
  group.forEach((e, i) => {
    const pair = `${e.reserveCategory}|${e.interestType}`;
    const cell = rateSets.get(pair) ?? { rates: [], order: orders[i] };
    cell.rates.push(e.rateCategory);
    cell.order = Math.min(cell.order, orders[i]);
    rateSets.set(pair, cell);
  });

  // (reserve, rate set) → interests
  const byReserveRates = new Map<
    string,
    {
      reserve: ReserveCategory;
      rates: RateCategory[];
      interests: InterestType[];
      order: number;
    }
  >();
  for (const [pair, cell] of rateSets) {
    const [reserve, interest] = pair.split('|') as [
      ReserveCategory,
      InterestType,
    ];
    const rates = inOrder(cell.rates, RATE_CATEGORIES);
    const key = `${reserve}|${rates.join(',')}`;
    const entry = byReserveRates.get(key) ?? {
      reserve,
      rates,
      interests: [],
      order: cell.order,
    };
    entry.interests.push(interest);
    entry.order = Math.min(entry.order, cell.order);
    byReserveRates.set(key, entry);
  }

  // (rate set, interest set) → reserves
  const { operator, rate, hurdleRate } = group[0];
  const blocks = new Map<string, Block>();
  for (const { reserve, rates, interests, order } of byReserveRates.values()) {
    const interestTypes = inOrder(interests, INTEREST_TYPES);
    const key = `${rates.join(',')}|${interestTypes.join(',')}`;
    const block = blocks.get(key) ?? {
      rateCategories: rates,
      reserveCategories: [],
      interestTypes,
      operator: operator || '',
      rate: toRate(rate),
      hurdleRate: toHurdle(hurdleRate),
      order,
    };
    block.reserveCategories.push(reserve);
    block.order = Math.min(block.order, order);
    blocks.set(key, block);
  }
  return [...blocks.values()].map((block) => ({
    ...block,
    reserveCategories: inOrder(block.reserveCategories, RESERVE_CATEGORIES),
  }));
}

/**
 * Groups combinations into editor lines: combinations sharing an operator, rate and hurdle go
 * on one line wherever they form a full rate × reserve × interest product. A no-hurdle reserve
 * category's blank hurdle matches any hurdle, so a PDP block joins a hurdle-bearing block with
 * the same rate and interest selections. Expanding the result gives back exactly the input
 * combinations; a repeated combination is kept on a line of its own so the overlap check still
 * reports it.
 * @param {DiscountRateCombination[]} entries - An area's combinations
 * @param {() => number} newId - Id generator for the lines
 * @return {DiscountRateLine[]} The lines, in the order of each one's first combination
 */
export function collapseEntries(
  entries: DiscountRateCombination[],
  newId: () => number
): DiscountRateLine[] {
  const seen = new Set<string>();
  const repeats: Block[] = [];
  const hurdleGroups = new Map<
    string,
    { members: DiscountRateCombination[]; orders: number[] }
  >();
  const noHurdleGroups = new Map<
    string,
    { members: DiscountRateCombination[]; orders: number[] }
  >();

  entries.forEach((e, order) => {
    const key = combinationKey(e);
    if (seen.has(key)) {
      repeats.push({
        rateCategories: [e.rateCategory],
        reserveCategories: [e.reserveCategory as ReserveCategory],
        interestTypes: [e.interestType],
        operator: e.operator || '',
        rate: toRate(e.rate),
        hurdleRate: toHurdle(e.hurdleRate),
        order,
      });
      return;
    }
    seen.add(key);

    const rateKey = `${e.operator || ''}|${toRate(e.rate)}`;
    const noHurdle = hasNoHurdle(e.reserveCategory);
    const groups = noHurdle ? noHurdleGroups : hurdleGroups;
    const groupKey = noHurdle
      ? rateKey
      : `${rateKey}|${toHurdle(e.hurdleRate) ?? ''}`;
    const group = groups.get(groupKey) ?? { members: [], orders: [] };
    group.members.push(noHurdle ? { ...e, hurdleRate: null } : e);
    group.orders.push(order);
    groups.set(groupKey, group);
  });

  const blocks = [...hurdleGroups.values()].flatMap(({ members, orders }) =>
    blocksOf(members, orders)
  );

  for (const { members, orders } of noHurdleGroups.values()) {
    for (const block of blocksOf(members, orders)) {
      const host = blocks.find(
        (candidate) =>
          candidate.operator === block.operator &&
          candidate.rate === block.rate &&
          candidate.rateCategories.join() === block.rateCategories.join() &&
          candidate.interestTypes.join() === block.interestTypes.join()
      );
      if (host) {
        host.reserveCategories = inOrder(
          [...host.reserveCategories, ...block.reserveCategories],
          RESERVE_CATEGORIES
        );
        host.order = Math.min(host.order, block.order);
      } else {
        blocks.push(block);
      }
    }
  }

  return [...blocks, ...repeats]
    .sort((a, b) => a.order - b.order)
    .map(({ order: _order, ...line }) => ({ id: newId(), ...line }));
}

// ─────────────────────────────────────
// Validation
// ─────────────────────────────────────

/**
 * @param {DiscountRateLine} line - An editor line
 * @return {boolean} Whether the line is complete: a selection in every multi-select and a
 *   non-negative rate (and hurdle, when set)
 */
export function isDiscountRateLineValid(line: DiscountRateLine): boolean {
  if (
    !line.rateCategories.length ||
    !line.reserveCategories.length ||
    !line.interestTypes.length
  )
    return false;
  if (line.rate === '' || Number(line.rate) < 0) return false;
  if (
    line.hurdleRate !== null &&
    line.hurdleRate !== '' &&
    Number(line.hurdleRate) < 0
  )
    return false;
  return true;
}

/**
 * @param {DiscountRateCombination} e - A combination
 * @param {CategoryLabels} labels - Category labels
 * @return {string} e.g. "Target / Proposal / Working Interest / Acme Oil"
 */
function describeCombination(
  e: DiscountRateCombination,
  labels: CategoryLabels
): string {
  const parts = [
    labelFor(labels.rateCategories, e.rateCategory),
    labelFor(labels.reserveCategories, e.reserveCategory),
    labelFor(labels.interestTypes, e.interestType),
  ];
  if (e.operator) parts.push(e.operator);
  return parts.join(' / ');
}

/**
 * The first combination two of an area's lines both cover — rate category + reserve category +
 * interest type + operator, the key the backend keeps unique.
 * @param {DiscountRateLine[]} lines - An area's lines
 * @param {CategoryLabels} labels - Category labels
 * @return {string | undefined} A save-blocking message naming the combination, if any
 */
export function overlappingCombinationMessage(
  lines: DiscountRateLine[],
  labels: CategoryLabels
): string | undefined {
  const seen = new Set<string>();
  for (const e of expandLines(lines)) {
    const key = combinationKey(e);
    if (seen.has(key)) {
      return `Two discount rate lines cover ${describeCombination(e, labels)}`;
    }
    seen.add(key);
  }
  return undefined;
}

/** Every value a line's three pickers can take, or the subset one offers. */
export interface CombinationUniverse {
  rateCategories: RateCategory[];
  reserveCategories: ReserveCategory[];
  interestTypes: InterestType[];
}

/**
 * @template T
 * @param {T[]} selected - One picker's selected values
 * @param {T[]} all - Every value the picker can take
 * @return {T[][]} The ways to fill the picker: the selection as it is, or each value on its own
 *   when nothing is selected
 */
function fillings<T>(selected: T[], all: T[]): T[][] {
  return selected.length > 0 ? [selected] : all.map((value) => [value]);
}

/**
 * @template T
 * @param {T[]} all - Every value one picker can take, in order
 * @param {T[]} selected - The picker's selected values, which stay offered
 * @param {(values: T[]) => boolean} completes - Whether a selection leaves every combination free
 * @return {T[]} The values the picker offers
 */
function offeredValues<T>(
  all: T[],
  selected: T[],
  completes: (values: T[]) => boolean
): T[] {
  return all.filter(
    (value) => selected.includes(value) || completes([...selected, value])
  );
}

/**
 * The values each of a line's pickers offers: those that, with what the line already selects,
 * complete a combination no other line of the area covers (the key overlappingCombinationMessage
 * checks at Save, so a line built from these never overlaps another). A picker with nothing
 * selected is free: some value of it only has to complete one free combination. The line's own
 * selections stay offered, so it can always be edited back. The key holds the operator, blank
 * being a value of its own, so a line only meets the lines with its own operator; with no operator
 * on any line it is ignored, and once one line has one it counts. Pass lineIndex null for a line
 * not yet added.
 * @param {DiscountRateLine[]} lines - The area's lines
 * @param {number | null} lineIndex - The line being filtered, or null for a new, blank one
 * @param {CombinationUniverse} universe - Every value a picker can take
 * @return {CombinationUniverse} The offered values, in universe order
 */
export function availableOptions(
  lines: DiscountRateLine[],
  lineIndex: number | null,
  universe: CombinationUniverse
): CombinationUniverse {
  const line = lineIndex === null ? undefined : lines[lineIndex];
  const operator = line?.operator ?? '';
  const covered = new Set(
    expandLines(lines.filter((_, i) => i !== lineIndex)).map(combinationKey)
  );
  const chosen: CombinationUniverse = {
    rateCategories: line?.rateCategories ?? [],
    reserveCategories: line?.reserveCategories ?? [],
    interestTypes: line?.interestTypes ?? [],
  };

  /**
   * @param {CombinationUniverse} selection - The pickers' selections
   * @return {boolean} Whether some way of filling the empty pickers (one value each) leaves every
   *   combination of the selections free
   */
  const completes = (selection: CombinationUniverse): boolean =>
    fillings(selection.rateCategories, universe.rateCategories).some((rates) =>
      fillings(selection.reserveCategories, universe.reserveCategories).some(
        (reserves) =>
          fillings(selection.interestTypes, universe.interestTypes).some(
            (interests) =>
              rates.every((rateCategory) =>
                reserves.every((reserveCategory) =>
                  interests.every(
                    (interestType) =>
                      !covered.has(
                        combinationKey({
                          rateCategory,
                          reserveCategory,
                          interestType,
                          operator,
                        })
                      )
                  )
                )
              )
          )
      )
    );

  return {
    rateCategories: offeredValues(
      universe.rateCategories,
      chosen.rateCategories,
      (rateCategories) => completes({ ...chosen, rateCategories })
    ),
    reserveCategories: offeredValues(
      universe.reserveCategories,
      chosen.reserveCategories,
      (reserveCategories) => completes({ ...chosen, reserveCategories })
    ),
    interestTypes: offeredValues(
      universe.interestTypes,
      chosen.interestTypes,
      (interestTypes) => completes({ ...chosen, interestTypes })
    ),
  };
}

/**
 * @param {DiscountRateLine[]} lines - The area's lines
 * @param {CombinationUniverse} universe - Every value a picker can take
 * @return {boolean} Whether a new, blank line could still take a combination no line covers
 */
export function hasFreeCombination(
  lines: DiscountRateLine[],
  universe: CombinationUniverse
): boolean {
  return availableOptions(lines, null, universe).rateCategories.length > 0;
}

/**
 * The first hurdle-bearing reserve category the area's lines give two different hurdle rates.
 * AreaConfig allows one hurdle per reserve category, so Save waits until they agree.
 * @param {DiscountRateLine[]} lines - An area's lines
 * @param {CategoryLabels} labels - Category labels
 * @return {string | undefined} A save-blocking message naming the category and its hurdles, if any
 */
export function hurdleConflictMessage(
  lines: DiscountRateLine[],
  labels: CategoryLabels
): string | undefined {
  const hurdles = new Map<ReserveCategory, Set<number>>();
  for (const line of lines) {
    if (line.hurdleRate === null || line.hurdleRate === '') continue;
    for (const reserve of line.reserveCategories) {
      if (hasNoHurdle(reserve)) continue;
      const values = hurdles.get(reserve) ?? new Set<number>();
      values.add(Number(line.hurdleRate));
      hurdles.set(reserve, values);
    }
  }
  for (const reserve of RESERVE_CATEGORIES) {
    const values = hurdles.get(reserve);
    if (!values || values.size < 2) continue;
    const list = [...values].sort((a, b) => a - b).map((v) => `${v}%`);
    return `${labelFor(labels.reserveCategories, reserve)} lines have different hurdle rates (${list.join(', ')})`;
  }
  return undefined;
}

/**
 * The combinations each line shares with another of the area's lines, so the editor can mark
 * the lines that block Save.
 * @param {DiscountRateLine[]} lines - An area's lines
 * @param {CategoryLabels} labels - Category labels
 * @return {Map<number, string[]>} Line id → the overlapping combinations it covers, described
 */
export function overlapsByLine(
  lines: DiscountRateLine[],
  labels: CategoryLabels
): Map<number, string[]> {
  const coverage = new Map<string, { label: string; lineIds: number[] }>();
  for (const line of lines) {
    for (const e of expandLine(line)) {
      const key = combinationKey(e);
      const covered = coverage.get(key);
      if (covered) covered.lineIds.push(line.id);
      else
        coverage.set(key, {
          label: describeCombination(e, labels),
          lineIds: [line.id],
        });
    }
  }

  const overlaps = new Map<number, string[]>();
  for (const { label, lineIds } of coverage.values()) {
    if (lineIds.length < 2) continue;
    for (const id of new Set(lineIds)) {
      overlaps.set(id, [...(overlaps.get(id) ?? []), label]);
    }
  }
  return overlaps;
}

interface RateGroup {
  reserve: ReserveCategory;
  interest: InterestType;
  operator: string;
  rates: Set<RateCategory>;
}

/**
 * @param {ReserveCategory} reserve - A reserve category
 * @param {InterestType} interest - An interest type
 * @param {string} operator - An operator, or blank
 * @return {string} A collision-free key for the three (an operator may contain any text)
 */
function rateGroupKey(
  reserve: ReserveCategory,
  interest: InterestType,
  operator: string
): string {
  return JSON.stringify([reserve, interest, operator]);
}

/**
 * The rate categories an area's rows give each reserve category + interest type + operator.
 * A row with a blank rate counts as present but gives no rate. Wherever an operator has rows,
 * the operator-less group is present too (empty if the area has none), since every other
 * operator's slots read it.
 * @param {DiscountRateCombination[]} entries - The area's combinations
 * @param {ReserveCategory[]} reserveCategories - The reserve categories to group
 * @return {Map<string, RateGroup>} The groups, keyed by rateGroupKey
 */
function rateGroups(
  entries: DiscountRateCombination[],
  reserveCategories: ReserveCategory[]
): Map<string, RateGroup> {
  const present = new Map<string, RateGroup>();
  for (const e of entries) {
    if (!e.reserveCategory || !reserveCategories.includes(e.reserveCategory))
      continue;
    const operator = e.operator || '';
    const key = rateGroupKey(e.reserveCategory, e.interestType, operator);
    const group = present.get(key) ?? {
      reserve: e.reserveCategory,
      interest: e.interestType,
      operator,
      rates: new Set<RateCategory>(),
    };
    if (e.rate !== '') group.rates.add(e.rateCategory);
    present.set(key, group);
  }
  for (const { reserve, interest, operator } of [...present.values()]) {
    const fallbackKey = rateGroupKey(reserve, interest, '');
    if (operator && !present.has(fallbackKey)) {
      present.set(fallbackKey, {
        reserve,
        interest,
        operator: '',
        rates: new Set<RateCategory>(),
      });
    }
  }
  return present;
}

/**
 * For each reserve category + interest type + operator an area has rows for, the rate
 * categories a slot there would get no rate for. An operator's row falls back to the area's
 * operator-less row of the same rate category, as RateResolver does, and an operator's rows
 * also need those operator-less rows (rateGroups). An interest type with no rows for a reserve
 * category is not reported.
 * @param {DiscountRateCombination[]} entries - The area's combinations
 * @param {CategoryLabels} labels - Category labels
 * @param {ReserveCategory[]} reserveCategories - The reserve categories to check
 * @return {string[]} One warning per incomplete reserve / interest / operator
 */
export function missingRateWarnings(
  entries: DiscountRateCombination[],
  labels: CategoryLabels,
  reserveCategories: ReserveCategory[] = RATE_COMPLETENESS_RESERVE_CATEGORIES
): string[] {
  const present = rateGroups(entries, reserveCategories);
  const warnings: { order: number[]; message: string }[] = [];
  for (const { reserve, interest, operator, rates } of present.values()) {
    const fallback = operator
      ? present.get(rateGroupKey(reserve, interest, ''))?.rates
      : undefined;
    const missing = RATE_CATEGORIES.filter(
      (rate) => !rates.has(rate) && !fallback?.has(rate)
    );
    if (!missing.length) continue;

    const subject = [
      labelFor(labels.reserveCategories, reserve),
      labelFor(labels.interestTypes, interest),
      ...(operator ? [operator] : []),
    ].join(' / ');
    const rateList = missing.map((rate) =>
      labelFor(labels.rateCategories, rate)
    );
    warnings.push({
      order: [
        RESERVE_CATEGORIES.indexOf(reserve),
        INTEREST_TYPES.indexOf(interest),
      ],
      message: `${subject} has no ${rateList.join(' or ')} rate`,
    });
  }
  warnings.sort((a, b) => a.order[0] - b.order[0] || a.order[1] - b.order[1]);
  return warnings.map(({ message }) => message);
}

// ─────────────────────────────────────
// Hurdle sync across lines
// ─────────────────────────────────────

/**
 * @param {ReserveCategory[]} reserves - Reserve categories
 * @return {ReserveCategory[]} Those whose hurdle syncs across the area
 */
function syncedReserves(reserves: ReserveCategory[]): ReserveCategory[] {
  return reserves.filter((r) => !hasNoHurdle(r));
}

/**
 * Sets a line's hurdle rate and syncs it to every other line sharing one of its hurdle-bearing
 * reserve categories — and on through those lines' categories — so each reserve category keeps
 * one hurdle rate across the area (the rule AreaConfig enforces). One edit also settles hurdles
 * that loaded or were left in conflict.
 * @param {DiscountRateLine[]} lines - An area's lines
 * @param {number} lineIndex - The edited line
 * @param {number | null} value - The new hurdle rate
 * @return {DiscountRateLine[]} The next lines
 */
export function setLineHurdle(
  lines: DiscountRateLine[],
  lineIndex: number,
  value: number | null
): DiscountRateLine[] {
  const target = lines[lineIndex];
  if (!target) return lines;

  const next = lines.map((line, i) =>
    i === lineIndex ? { ...line, hurdleRate: value } : line
  );
  const synced = new Set(syncedReserves(target.reserveCategories));
  const reached = new Set([lineIndex]);

  let grew = synced.size > 0;
  while (grew) {
    grew = false;
    next.forEach((line, i) => {
      if (reached.has(i)) return;
      if (!line.reserveCategories.some((r) => synced.has(r))) return;
      reached.add(i);
      next[i] = { ...line, hurdleRate: value };
      syncedReserves(line.reserveCategories).forEach((r) => synced.add(r));
      grew = true;
    });
  }
  return next;
}

/**
 * Changes a line's reserve categories, never touching another line. A line left with only
 * no-hurdle categories drops its hurdle. A line with a blank hurdle that gains a hurdle-bearing
 * category takes the hurdle the other lines give its categories, when they all agree
 * on one. A line that already has a hurdle keeps it; if a gained category carries a different
 * one elsewhere, hurdleConflictMessage blocks Save until the user settles it.
 * @param {DiscountRateLine[]} lines - An area's lines
 * @param {number} lineIndex - The edited line
 * @param {ReserveCategory[]} reserveCategories - The line's new selection
 * @return {DiscountRateLine[]} The next lines
 */
export function setLineReserveCategories(
  lines: DiscountRateLine[],
  lineIndex: number,
  reserveCategories: ReserveCategory[]
): DiscountRateLine[] {
  const target = lines[lineIndex];
  if (!target) return lines;

  const reserves = inOrder(reserveCategories, RESERVE_CATEGORIES);
  const updated: DiscountRateLine = { ...target, reserveCategories: reserves };
  if (isLineHurdleDisabled(updated)) updated.hurdleRate = null;
  const next = lines.map((line, i) => (i === lineIndex ? updated : line));

  const added = syncedReserves(
    reserves.filter((r) => !target.reserveCategories.includes(r))
  );
  if (!added.length) return next;
  if (updated.hurdleRate !== null && updated.hurdleRate !== '') return next;

  const synced = syncedReserves(reserves);
  const shared = new Set(
    next
      .filter(
        (line, i) =>
          i !== lineIndex &&
          line.hurdleRate !== null &&
          line.hurdleRate !== '' &&
          line.reserveCategories.some((r) => synced.includes(r))
      )
      .map((line) => Number(line.hurdleRate))
  );
  if (shared.size !== 1) return next;
  return next.map((line, i) =>
    i === lineIndex ? { ...line, hurdleRate: [...shared][0] } : line
  );
}
